import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import { useAuth } from '@store/auth/AuthProvider'

export interface CartItem {
  id: number
  matnr: string
  nombre: string
  unidades: number
  pvf: string
  ok: string
  estado: string
}

interface CartContextType {
  items: CartItem[]
  count: number
  isLoading: boolean
  isOpen: boolean
  setOpen: (open: boolean) => void
  refresh: () => Promise<void>
  addItem: (matnr: string, arktx: string, unidades: number) => Promise<boolean>
  updateQty: (itemId: number, unidades: number) => Promise<void>
  removeItem: (itemId: number) => Promise<void>
  clearCart: () => Promise<void>
  confirmOrder: (tipo?: string) => Promise<{ success: boolean; message: string; itemCount: number }>
}

const CartContext = createContext<CartContextType | null>(null)

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('ecofarte_token')}`,
  'Content-Type': 'application/json'
})

export function CartProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()
  const [items, setItems] = useState<CartItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const refreshRef = useRef<() => Promise<void>>()

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/cart', { headers: authHeaders() })
      if (res.ok) {
        const json = await res.json()
        if (json.success) {
          setItems(json.data.items)
        }
      }
    } catch {
      // silent
    }
  }, [])

  refreshRef.current = refresh

  useEffect(() => {
    if (!isAuthenticated) {
      setItems([])
      return
    }
    void refresh()
  }, [isAuthenticated, refresh])

  const addItem = useCallback(async (matnr: string, arktx: string, unidades: number): Promise<boolean> => {
    try {
      const res = await fetch('/api/cart/items', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ matnr, arktx, unidades })
      })
      if (res.ok) {
        const json = await res.json()
        if (json.success) {
          await refresh()
          return true
        }
      }
      return false
    } catch {
      return false
    }
  }, [refresh])

  const updateQty = useCallback(async (itemId: number, unidades: number) => {
    try {
      const res = await fetch(`/api/cart/items/${itemId}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ unidades })
      })
      if (res.ok) {
        const json = await res.json()
        if (json.success) {
          await refresh()
        }
      }
    } catch {
      // silent
    }
  }, [refresh])

  const removeItem = useCallback(async (itemId: number) => {
    try {
      const res = await fetch(`/api/cart/items/${itemId}`, {
        method: 'DELETE',
        headers: authHeaders()
      })
      if (res.ok) {
        const json = await res.json()
        if (json.success) {
          await refresh()
        }
      }
    } catch {
      // silent
    }
  }, [refresh])

  const clearCart = useCallback(async () => {
    try {
      const res = await fetch('/api/cart', {
        method: 'DELETE',
        headers: authHeaders()
      })
      if (res.ok) {
        await refresh()
      }
    } catch {
      // silent
    }
  }, [refresh])

  const confirmOrder = useCallback(async (tipo: string = 'ZWEB') => {
    setIsLoading(true)
    try {
      const res = await fetch('/api/cart/confirm', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ tipo })
      })
      const json = await res.json()
      if (json.success) {
        setItems([])
        return { success: true, message: json.data.message, itemCount: json.data.itemCount }
      } else {
        const errorMsg = json.error?.message ?? (typeof json.error === 'string' ? json.error : '')
        return { success: false, message: errorMsg || 'Error al enviar el pedido', itemCount: 0 }
      }
    } catch {
      return { success: false, message: 'Error de conexion', itemCount: 0 }
    } finally {
      setIsLoading(false)
    }
  }, [])

  const value: CartContextType = {
    items,
    count: items.length,
    isLoading,
    isOpen,
    setOpen: setIsOpen,
    refresh,
    addItem,
    updateQty,
    removeItem,
    clearCart,
    confirmOrder
  }

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  )
}

export function useCart(): CartContextType {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart debe usarse dentro de CartProvider')
  }
  return context
}
